<template>
  <div class="kt-portlet kt-portlet--height-fluid">
    <div
      class="kt-portlet__head kt-portlet__head kt-portlet__head--lg kt-portlet__head--noborder kt-portlet__head--break-sm"
    >
      <div class="kt-portlet__head-label">
        <span class="kt-portlet__head-icon">
          <i class="fal fa-books"></i>
        </span>
        <h3 class="kt-portlet__head-title">Module Manager</h3>
      </div>
    </div>
    <div class="kt-portlet__body">
      <v-data-table
        :headers="headers"
        :items="modules"
        sort-by="name"
        class="elevation-1"
        :items-per-page="pagination.per_page"
        :server-items-length="pagination.total"
        :loading="loading.fetching"
        :disabled="loading.fetching"
        @update:page="changePage"
        @update:sort-by="updateSorting"
        @update:sort-desc="updateSorting"
        :options.sync="options"
      >
        <template v-slot:top>
          <div class="row no-gutters px-3 mt-3">
            <div class="col-xl-4">
              <v-select
                label="Filter Status"
                :loading="loading.fetching"
                :disabled="loading.fetching"
                @change="fetchModules"
                dense
                v-model="filters"
                multiple
                prepend-inner-icon="fas fa-filter"
                :items="[
                  'Active',
                  'Inactive',
                  'Published',
                  'Drafts',
                  'Uncategorized',
                ]"
              >
                <template v-slot:prepend-item>
                  <v-list-item
                    v-if="filters.length <= 0"
                    ripple
                    @click="toggleAllFilter"
                  >
                    <v-list-item-content>
                      <v-list-item-title>All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-else ripple @click="removeAllFilter">
                    <v-list-item-content>
                      <v-list-item-title>Deselect All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2" />
                </template>
              </v-select>
            </div>
            <div class="col-xl-1"></div>
            <div class="col-xl-7">
              <div class="d-flex flex-wrap flex-sm-nowrap">
                <v-text-field
                  class="mt-0 pt-1 mr-0 mr-sm-5 w-100"
                  clearable
                  v-model.trim="search"
                  @change="searchModules"
                  append-icon="fal fa-search"
                  label="Search (By Name, Course, Curriculum, Company, Author, Last Edited By)"
                  single-line
                  hide-details
                />
                <v-btn
                  color="primary"
                  dark
                  class="mx-auto mt-3 mt-sm-0"
                  @click="toggleAddModuleDialog"
                  :disabled="loading.fetching"
                  :loading="loading.fetching"
                  text
                >
                  <v-icon class="mr-2" small>fal fa-plus </v-icon>
                  New Module
                </v-btn>
              </div>
            </div>
          </div>
        </template>
        <template v-slot:item.action="{ item }">
          <template v-if="item.deleted_at === null">
            <v-btn
              text
              @click="toggleEditModuleDialog(item)"
              color="grey darken-3"
              small
            >
              <v-icon small>fal fa-edit</v-icon>
            </v-btn>
            <v-btn text @click="deleteItem(item)" color="red darken-3" small>
              <v-icon small>fal fa-trash</v-icon>
            </v-btn>
          </template>
          <template v-else>
            <v-btn text @click="restoreItem(item)" color="blue darken-3" small>
              <v-icon small class="mr-2">fal fa-trash-restore</v-icon>
              Restore
            </v-btn>
          </template>
        </template>
        <template v-slot:no-data>
          <div class="mt-1 mb-1">
            No data available <br />
            <v-btn color="primary" @click="fetchModules">Reset</v-btn>
          </div>
        </template>
        <template v-slot:item.name="{ item }">
          <v-list-item>
            <v-list-item-avatar tile size="40">
              <v-img
                aspect-ratio="1"
                :src="
                  includes(
                    imageFileTypes,
                    item.thumbnail
                      ? item.thumbnail.split('.').slice(-1).toString()
                      : false
                  )
                    ? item.thumbnail
                    : 'https://www.ppxray.com/wp-content/themes/ppxray.com/images/thumbnail.png'
                "
              ></v-img>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>
                <v-tooltip v-if="item.name.length > 25" top>
                  <template #activator="{ on }">
                    <h4 v-on="on">{{ trimString(item.name) }}</h4>
                  </template>
                  <span>{{ item.name }}</span>
                </v-tooltip>
                <h4 v-else>{{ item.name }}</h4>
              </v-list-item-title>
              <v-list-item-subtitle
                v-html="'Created By: ' + item.author.name"
              ></v-list-item-subtitle>
              <v-list-item-subtitle
                color="error"
                v-html="'Last Edited By: ' + item.last_edit_by.name"
              ></v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>
        <template v-slot:item.course="{ item }">
          <v-list-item-content v-if="item.course">
            <v-list-item-title>
              <h6>{{ item.course.name }}</h6>
            </v-list-item-title>
            <v-list-item-subtitle
              v-if="item.course.curriculum"
              v-html="item.course.curriculum.name"
            ></v-list-item-subtitle>
            <v-list-item-subtitle
              v-if="item.course.curriculum"
              v-html="item.course.curriculum.company_author.name"
            ></v-list-item-subtitle>
          </v-list-item-content>
        </template>
        <template v-slot:item.is_draft="{ item }">
          <span :class="item.is_draft ? '' : 'text-success'">
            {{ item.is_draft ? 'Draft' : 'Published' }}
          </span>
        </template>
        <template v-slot:item.created_at="{ item }">{{
          item.created_at | displayDateTime
        }}</template>
      </v-data-table>
    </div>
    <add-module-dialog
      :add-module-dialog="addModuleDialog"
      :toggle-add-module-dialog="toggleAddModuleDialog"
      :closeModal="toggleClose"
      v-on:addedModule="fetchModules"
      v-on:added-module="fetchModules"
    ></add-module-dialog>
    <edit-module-dialog
      :module="editedItem"
      :edit-module-dialog="editModuleDialog"
      :toggle-edit-module-dialog="toggleEditModuleDialog"
      v-on:module="fetchModules"
      v-on:close-dialog="toggleClose"
      :key="key"
      :show-curriculum="showCurriculum"
    ></edit-module-dialog>
    <confirm-dialog
      :loading="loading.deleting"
      :show-dialog="showDeleteConfirmDialog"
      :close-dialog="closeConfirmDeleteDialog"
      :confirm-action="proceedToDeletion"
    >
      Are you sure you want to proceed?
    </confirm-dialog>
  </div>
</template>
<script>
import {
  head as _head,
  sortBy as _sortBy,
  includes as _includes,
} from 'lodash-es';
import WordEditor from '../WordEditor.vue';
import EventBus from '../../plugins/eventBus';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog';
import AddModuleDialog from '@/components/course-certification/includes/AddModuleDialog';
import EditModuleDialog from '@/components/course-certification/includes/EditModuleDialog';

export default {
  name: 'ModuleSection',
  components: {
    ConfirmDialog,
    WordEditor,
    AddModuleDialog,
    EditModuleDialog,
  },
  data() {
    return {
      addModuleDialog: false,
      editModuleDialog: false,
      includes: _includes,
      imageFileTypes: ['jpg', 'png', 'webp'],
      showDeleteConfirmDialog: false,
      selectedIdForDeletion: '',
      fileUploadOrLink: false,
      loading: {
        fetching: false,
        saving: false,
        deleting: false,
      },
      filters: '',
      search: '',
      moduleFormDialog: false,
      thumbnailLink: '',
      categories: [],
      headers: [
        {
          text: 'Module Name',
          align: 'left',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Course',
          align: 'left',
          sortable: true,
          value: 'course',
        },
        {
          text: 'Status',
          align: 'center',
          sortable: true,
          value: 'is_draft',
        },
        {
          text: 'Date Created',
          align: 'center',
          sortable: true,
          value: 'created_at',
        },
        {
          text: 'Action',
          align: 'center',
          value: 'action',
          width: 200,
          sortable: false,
        },
      ],
      editedIndex: -1,
      editedItem: {
        id: '',
        name: '',
        category_id: '',
        link: '',
        image: [],
        description: '',
        call_guide_link: '',
        links: [],
      },
      defaultItem: {
        id: '',
        name: '',
        category_id: '',
        link: '',
        image: [],
        description: '',
        call_guide_link: '',
      },
      pagination: {
        total: 1,
        per_page: 5,
        current_page: 1,
      },
      sortBy: [],
      noFileUpload: {
        display: false,
        message: '',
      },
      options: {
        groupBy: [],
        groupDesc: [],
        itemsPerPage: 5,
        multiSort: false,
        mustSort: false,
        page: 1,
        sortBy: ['created_at'],
        sortDesc: [true],
        categories: [],
      },
      course_id: null,
      fetchingCategories: false,
      key: 0,
      showCurriculum: true,
    };
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'New Module' : 'Edit Module';
    },
    courses() {
      return this.$store.state.courses.listCourses;
    },
    modules() {
      if (this.sortBy) {
        return _sortBy(this.$store.state.modules.modulesList);
      }
      return this.$store.state.modules.modulesList;
    },
  },

  watch: {
    moduleFormDialog(val) {
      // eslint-disable-next-line
      val || this.close();
    },
  },

  created() {
    this.toggleAllFilter();
  },

  mounted() {
    this.$validator.localize('en', {
      attributes: {
        category_id: 'category',
      },
    });
  },

  methods: {
    trimString(string) {
      let count = 20;

      if (string.length <= count) {
        return string;
      } else {
        return string.slice(0, count) + '...';
      }
    },

    forceRerender() {
      this.key += 1;
    },

    toggleAddModuleDialog() {
      this.addModuleDialog = !this.addModuleDialog;
    },

    toggleEditModuleDialog(item = null) {
      this.editedItem = item;
      this.editedItem.curriculum_id = item.course
        ? item.course.curriculum_id
        : null;
      this.forceRerender();
      this.editModuleDialog = !this.editModuleDialog;
    },

    toggleClose() {
      EventBus.$emit('RELOAD_TABLE');
      this.addModuleDialog = false;
      this.editModuleDialog = false;
    },

    fetchCategories() {
      this.fetchingCategories = true;
      this.axios
        .get('lms-manager/fetch/categoriesfromcourse', {
          params: {
            course_id: this.course_id,
          },
        })
        .then(({ data }) => {
          this.categories = data;
          this.fetchingCategories = false;
        })
        .catch((error) => {
          console.log('something went wrong with fetching categories');
          this.fetchingCategories = false;
        });
    },

    closeConfirmDeleteDialog() {
      this.showDeleteConfirmDialog = false;
      this.selectedIdForDeletion = '';
      this.loading.deleting = false;
    },

    sort(val) {
      this.sortBy = _head(val);
    },

    updateSorting() {
      this.pagination.current_page = 1;
      this.fetchModules();
    },

    searchModules() {
      (this.options.page = 1), (this.pagination.current_page = 1);
      this.fetchModules();
    },

    changePage(page) {
      this.pagination.current_page = page;
      this.fetchModules();
    },

    toggleAllFilter() {
      this.filters = [
        'Active',
        'Inactive',
        'Published',
        'Drafts',
        'Uncategorized',
      ];
      this.fetchModules();
    },

    removeAllFilter() {
      this.filters = [];
      this.fetchModules();
    },

    fetchModules() {
      this.loading.fetching = true;
      const filter =
        this.filters.length >= 5
          ? ['all']
          : this.filters.map(function (filter) {
              return filter.toLowerCase();
            });
      const search = this.search !== null ? this.search : '';
      this.axios
        .get('/lms-manager/modules', {
          params: {
            page: this.pagination.current_page,
            per_page: this.options.itemsPerPage,
            type: filter,
            search: search,
            sortBy: this.options.sortBy[0],
            sortDesc: this.options.sortDesc[0],
          },
        })
        .then(({ data }) => {
          this.$store.commit('modules/setModules', data.data);
          this.pagination.total = data.total;
          this.pagination.current_page = data.current_page;
          this.pagination.per_page = Number(data.per_page);
        })
        .finally(() => {
          this.loading.fetching = false;
        });
    },

    editItem(item) {
      this.editedIndex = this.modules.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.thumbnailLink = item.thumbnail;
      this.course_id = this.editedItem.category.course_id;
      this.fetchCategories();
      this.moduleFormDialog = true;
    },

    deleteItem(item) {
      this.selectedIdForDeletion = item.id;
      this.showDeleteConfirmDialog = true;
    },

    proceedToDeletion() {
      this.loading.deleting = true;
      this.$store
        .dispatch('modules/deleteModule', this.selectedIdForDeletion)
        .then(() => {
          this.$notify('success', 'Module removed!');
          // eslint-disable-next-line no-undef
          EventBus.$emit('RELOAD_TABLE');
          this.fetchModules();
          this.loading.deleting = false;
          this.closeConfirmDeleteDialog();
        });
    },

    restoreItem(item) {
      const { id } = item;
      this.$swal({
        title: 'Are you sure?',
        text: 'This action will put the module to active.',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.value) {
          this.$store.dispatch('modules/restoreModule', id).then(() => {
            this.$notify('success', 'Module restored');
            this.fetchModules();
            EventBus.$emit('RELOAD_TABLE');
          });
        }
      });
    },

    close() {
      this.$validator.reset();
      this.moduleFormDialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },

    validate(callback) {
      this.$validator.validate('*').then((result) => {
        if (result) {
          callback();
        }
      });
    },

    save() {
      if (this.editedIndex > -1) {
        // Object.assign(this.s[this.editedIndex], this.editedItem);
        this.validate(this.update);
      } else {
        this.validate(this.store);
      }
    },

    setFormData() {
      const formData = new FormData();
      formData.append('name', this.editedItem.name);
      formData.append('description', this.editedItem.description);
      formData.append('call_guide_link', this.editedItem.call_guide_link);
      formData.append('category_id', this.editedItem.category_id);

      if (this.fileUploadOrLink) {
        formData.append('link', this.editedItem.link);
      }

      if (!this.fileUploadOrLink) {
        formData.append('thumbnail', this.editedItem.image);
      }

      return formData;
    },

    store() {
      this.loading.saving = true;
      this.$store
        .dispatch('modules/saveModule', this.setFormData())
        .then(() => {
          this.$notify('success', 'Module saved!');
          this.fetchModules();
          this.close();
        })
        .catch((error) => {
          this.interactWithErrorBag(error, this.errors);
        })
        // eslint-disable-next-line no-return-assign
        .finally(() => (this.loading.saving = false));
    },

    update() {
      this.loading.saving = true;
      const payload = this.setFormData();
      this.$store
        .dispatch('modules/editModule', {
          payload,
          id: this.editedItem.id,
        })
        .then(() => {
          this.$notify('success', 'Module updated!');
          this.fetchModules();
          this.close();
        })
        .catch((error) => {
          this.interactWithErrorBag(error, this.errors);
        })
        // eslint-disable-next-line no-return-assign
        .finally(() => (this.loading.saving = false));
    },
  },
};
</script>
<style lang="stylus">
.highlight {
  background-color: yellow;
}
.module-btn {
  margin: 0px 4px;
}
@media screen and (max-width: 604px) {
  .module-btn{
    display: block!important;
    margin: 4px auto;
  }
}

@media screen and (max-width: 599px) {
  .module-btn {
    display: inline-block!important;
    margin: 0px 4px;
  }

}
</style>
